import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { PRIMARY_COLORS, LIGHT_GRAY } from '../../common/colors';
import { CardAuthors } from './Byline';

// timeZone: 'UTC' anchors the (time-less) post date to a fixed instant so
// the rendered day doesn't depend on the reader's offset, and so server
// prerender and client hydration agree. Locale still follows the visitor's
// language (i18n.language) so the wording adapts, e.g. "1 janvier 2026".
export const formatDate = (date, locale = 'en-US') =>
  new Date(date).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });

// Viewport width from which the blog grid has three columns and the featured
// card spans two of them with its image beside the text. Not MUI's md
// (900px): at 900px a third of the content width is only ~268px, which
// squeezes the titles.
export const THREE_COLUMNS_UP = '@media (min-width: 1000px)';

const clampLines = lines => ({ display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical', overflow: 'hidden' });

// Blog post card, on the blog list and the home page's highlights. Compact
// so a screen holds whole rows of them: title and excerpt are clamped to
// three lines and the date shares the meta line. `featured` is the first
// post on the blog list — two grid columns wide from THREE_COLUMNS_UP with
// the image beside unclamped text, centred vertically. Below that it is a
// regular-sized card with the image on top. Posts with no image (no
// frontmatter cover, no image in the body) skip the image slot entirely
// rather than showing an empty placeholder.
//
// The image slot is a fixed 16:9 so a thumbnail crops the same on every
// layout. How the image fills it — `imageFit` and `imagePosition` — is
// decided at build time from the image's shape and the post's `imageCrop:`
// frontmatter (build-blog-index.js cardImageFit).
//
// The side-by-side featured card is the exception (ocl_online#158, #161).
// Its height comes from the row it shares with a regular card (~436px), and
// a 16:9 image that tall would be wider than the whole card, so the slot
// stretches to the card's height instead. Cropping the image to fill that
// slot cuts the sides off landscape card art, so the image is shown whole
// (`contain`) over a blurred, cropped copy of itself that fills the rest.
const PostCard = ({ post, featured = false }) => {
  const { t, i18n } = useTranslation();
  const hasImage = Boolean(post.image);
  const sideBySide = featured && hasImage;
  return (
    <Box
      component={RouterLink}
      to={`/blog/${post.slug}`}
      sx={{
        display: sideBySide ? 'grid' : (featured ? 'flex' : 'block'),
        flexDirection: 'column',
        ...(sideBySide ? { [THREE_COLUMNS_UP]: { gridTemplateColumns: '3fr 2fr' } } : {}),
        textDecoration: 'none',
        backgroundColor: 'background.paper',
        border: `1px solid ${LIGHT_GRAY}`,
        borderRadius: '12px',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 3 },
      }}>
      {hasImage && (
        <Box
          sx={{
            position: 'relative',
            aspectRatio: '16 / 9',
            alignSelf: 'start',
            ...(sideBySide ? { [THREE_COLUMNS_UP]: { aspectRatio: 'auto', alignSelf: 'stretch', overflow: 'hidden' } } : {}),
          }}>
          {sideBySide && (
            <Box
              component="img"
              src={post.image}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              sx={{
                display: 'none',
                [THREE_COLUMNS_UP]: {
                  display: 'block',
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  filter: 'blur(24px)',
                  transform: 'scale(1.2)',
                },
              }}
            />
          )}
          <Box
            component="img"
            src={post.image}
            alt=""
            loading="lazy"
            decoding="async"
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: post.imageFit === 'contain' ? 'contain' : 'cover',
              objectPosition: post.imagePosition || undefined,
              display: 'block',
              ...(sideBySide ? { [THREE_COLUMNS_UP]: { objectFit: 'contain', objectPosition: 'center' } } : {}),
            }}
          />
        </Box>
      )}
      <Box sx={{ p: 2.5, ...(featured ? { display: 'flex', flexDirection: 'column', justifyContent: 'center', flexGrow: 1 } : {}) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
          {post.category && (
            <Chip label={post.category} size="small" sx={{ backgroundColor: PRIMARY_COLORS['95'], color: 'primary.main', fontWeight: 600 }} />
          )}
          <Typography variant="body2" sx={{ color: 'surface.contrastText' }}>
            {`${formatDate(post.date, i18n.language)} · ${t('common.min_read', { count: post.readMinutes })}`}
          </Typography>
        </Box>
        <Typography
          variant={featured ? 'h4' : 'h6'}
          component="h3"
          sx={{ color: 'surface.dark', mb: 1, ...(featured ? { fontSize: '1.5rem', ...(hasImage ? {} : { [THREE_COLUMNS_UP]: { fontSize: '2rem' } }) } : clampLines(3)) }}>
          {post.title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'surface.contrastText', ...(featured ? {} : clampLines(3)) }}>
          {post.excerpt}
        </Typography>
        {post.authors.length > 0 && <CardAuthors authors={post.authors} />}
      </Box>
    </Box>
  );
};

export default PostCard;
